import ConfigBusiness from '../config/ConfigBusiness';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt-br';

var DEFAULT_LOCALE = 'es-AR';

var _cachedLocale = null;
var _cachedMomentLocale = null;

/**
 * Returns the configured locale string (e.g., 'es-AR', 'en-US', 'pt-BR').
 * Defaults to 'es-AR' when not configured.
 */
function getLocale() {
  if (_cachedLocale) return _cachedLocale;
  var value = ConfigBusiness.get('generales.formatoRegional');
  if (value && typeof value === 'string' && value.length > 0) {
    _cachedLocale = value;
    return value;
  }
  // Don't cache the fallback — configBusiness may not be loaded yet
  return DEFAULT_LOCALE;
}

/**
 * Returns the moment.js compatible locale string.
 * Moment uses lowercase with hyphen (e.g., 'es', 'pt-br', 'en')
 * and falls back to the language prefix if the full locale isn't available.
 */
function getMomentLocale() {
  if (_cachedMomentLocale) return _cachedMomentLocale;
  var locale = getLocale().replace('_', '-').toLowerCase();
  var availableLocales = moment.locales();
  var prefix = locale.split('-')[0];
  var resolved;
  // Try full locale first (e.g., 'pt-br'), then language prefix (e.g., 'es')
  if (availableLocales.indexOf(locale) !== -1) {
    resolved = locale;
  } else if (availableLocales.indexOf(prefix) !== -1) {
    resolved = prefix;
  } else {
    resolved = 'es';
  }
  // Only cache when derived from a real config value, not the fallback
  if (_cachedLocale) {
    _cachedMomentLocale = resolved;
  }
  return resolved;
}

/**
 * Clears the cached locale. Call when config changes (e.g., login, config reload).
 */
function refreshFormats() {
  _cachedLocale = null;
  _cachedMomentLocale = null;
}

/**
 * Returns the locale string for use with toLocaleString() and Intl.NumberFormat.
 */
function getNumberLocale() {
  return getLocale();
}

/**
 * Formats a date value for display using the configured locale.
 * Returns empty string for falsy values.
 */
function formatDate(value) {
  if (!value) return '';
  var m = moment(value);
  if (!m.isValid()) return '';
  return m.locale(getMomentLocale()).format('L');
}

/**
 * Formats a datetime value for display using the configured locale.
 * Returns empty string for falsy values.
 */
function formatDateTime(value) {
  if (!value) return '';
  var m = moment(value);
  if (!m.isValid()) return '';
  return m.locale(getMomentLocale()).format('L HH:mm');
}

/**
 * Formats a number for display using the configured locale.
 * Returns the original value for non-numeric input.
 */
function formatNumber(value, decimals) {
  if (value === null || value === undefined || value === '') return value;
  var num = Number(value);
  if (isNaN(num)) return value;
  var opts = {};
  if (decimals !== undefined && decimals !== null) {
    opts.minimumFractionDigits = decimals;
    opts.maximumFractionDigits = decimals;
  }
  return num.toLocaleString(getLocale(), opts);
}

/**
 * Formats a currency value for display using the configured locale.
 */
function formatCurrency(value, currency) {
  if (value === null || value === undefined) return '';
  var num = Number(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency || 'ARS'
  }).format(num);
}

// DatePicker format strings by language prefix
var DATEPICKER_DATE_FORMATS = {
  es: 'DD/MM/YYYY',
  pt: 'DD/MM/YYYY',
  en: 'MM/DD/YYYY',
};

var DATEPICKER_DATETIME_FORMATS = {
  es: 'DD/MM/YYYY HH:mm',
  pt: 'DD/MM/YYYY HH:mm',
  en: 'MM/DD/YYYY HH:mm',
};

/**
 * Returns the date format string for DatePicker dateFormat prop.
 */
function getDatePickerFormat() {
  var prefix = getLocale().split(/[-_]/)[0].toLowerCase();
  return DATEPICKER_DATE_FORMATS[prefix] || 'DD/MM/YYYY';
}

/**
 * Returns the datetime format string for DatePicker dateFormat prop.
 */
function getDateTimePickerFormat() {
  var prefix = getLocale().split(/[-_]/)[0].toLowerCase();
  return DATEPICKER_DATETIME_FORMATS[prefix] || 'DD/MM/YYYY HH:mm';
}

var LocaleFormat = {
  getLocale: getLocale,
  getNumberLocale: getNumberLocale,
  refreshFormats: refreshFormats,
  formatDate: formatDate,
  formatDateTime: formatDateTime,
  formatNumber: formatNumber,
  formatCurrency: formatCurrency,
  getDatePickerFormat: getDatePickerFormat,
  getDateTimePickerFormat: getDateTimePickerFormat,
};

export default LocaleFormat;
